import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ISecretDTO } from "../interfaces/ISecretDTO";
import { VaultCardUI } from "../component/atoms/VaultCard";
import { createPasswordVaultAction } from "../actions/create-password.action";
import { fetchPasswordsAction } from "../actions/get-all-password.action";
import { deletePasswordVaultAction } from "../actions/delete-password.action";
import { updatePasswordVaultAction } from "../actions/update-password.action";
import { fetchPasswordVaultStatus } from "../actions/get-one-password.action";

const HIGH_SECURITY_VERIFY_SECONDS = 60;

type UseVaultLogicOptions = {
    onAutoLock?: () => void; // <- call when timer hits 0
    isPreviewMode?: boolean;
};

export const useVaultLogic = (unlocked: boolean, options?: UseVaultLogicOptions) => {
    const queryClient = useQueryClient();
    const [search, setSearch] = useState("");
    const [verifiedUntilById, setVerifiedUntilById] = useState<Record<string, number>>({});

    const [vaultRemainingSeconds, setVaultRemainingSeconds] = useState<number>(0);
    const vaultIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const stopVaultTimer = useCallback(() => {
        if (vaultIntervalRef.current) {
            clearInterval(vaultIntervalRef.current);
            vaultIntervalRef.current = null;
        }
        setVaultRemainingSeconds(0);
    }, []);

    const startVaultTimer = useCallback(
        (seconds: number) => {
            const s = Math.max(0, Math.floor(seconds || 0));
            setVaultRemainingSeconds(s);

            if (vaultIntervalRef.current) clearInterval(vaultIntervalRef.current);

            vaultIntervalRef.current = setInterval(() => {
                setVaultRemainingSeconds((prev) => {
                    const next = Math.max(0, prev - 1);

                    if (next === 0) {
                        // hit 0 -> stop timer & autolock
                        if (vaultIntervalRef.current) {
                            clearInterval(vaultIntervalRef.current);
                            vaultIntervalRef.current = null;
                        }
                        options?.onAutoLock?.();
                    }

                    return next;
                });
            }, 1000);
        },
        [options]
    );

    // If app locks vault externally, stop timer
    useEffect(() => {
        if (!unlocked) stopVaultTimer();
        return () => stopVaultTimer();
    }, [unlocked, stopVaultTimer]);


    const { data: list = [] } = useQuery<ISecretDTO[]>({
        queryKey: ["passwords"],
        queryFn: fetchPasswordsAction,
        enabled: unlocked && !options?.isPreviewMode,
    });

    const mockSecret: ISecretDTO = useMemo(() => ({
        id: "mock123",
        title: "ConnectaCleaner Master Key",
        usernameEmail: "admin@connectacleaner.com",
        password: "SecretPassword123!",
        websiteUrl: "https://connectacleaner.com",
        notes: "This is a default mock entry in your password vault preview. Unlock the feature to add and manage your own passwords safely.",
        highSecurity: false,
    }), []);

    const sourceData = useMemo(() => options?.isPreviewMode ? [mockSecret] : list, [list, mockSecret, options?.isPreviewMode]);

    const cards = useMemo(() => {
        return sourceData.map((dto: any) => ({
            id: String(dto.id ?? crypto.randomUUID()),
            title: String(dto.title ?? ""),
            usernameEmail: String(dto.usernameEmail ?? ""),
            imageUrl: dto.imageUrl ?? null,
            highSecurity: Boolean(dto.highSecurity),
            password: String(dto.password ?? ""),
        }));
    }, [sourceData]);

    const secretById = useMemo(() => {
        const byId: Record<string, ISecretDTO> = {};
        cards.forEach((c, i) => {
            byId[c.id] = sourceData[i];
        });
        return byId;
    }, [cards, sourceData]);


    /**
     * This triggers the backend to send a 2FA code.
     * The backend sends 2FA automatically when fetchPasswordVaultStatus is called.
     */
    const getOneMutation = useMutation({
        mutationFn: ({ id, code }: { id: string | number; code?: string }) =>
            fetchPasswordVaultStatus(id, code),
    });

    const sendTwoFaTrigger = useCallback(
        (args: { id: string | number; code?: string }) => getOneMutation.mutateAsync(args),
        [getOneMutation]
    );


    const addMutation = useMutation({
        mutationFn: ({ dto, imageFile }: { dto: ISecretDTO; imageFile: File | null }) =>
            createPasswordVaultAction(dto, imageFile),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["passwords"] });
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, dto }: { id: string; dto: ISecretDTO }) =>
            updatePasswordVaultAction(id, dto),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["passwords"] });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => deletePasswordVaultAction(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["passwords"] });
        },
    });

    const handleAddSecret = async (dto: ISecretDTO, imageFile: File | null) => {
        if (options?.isPreviewMode) return;
        await addMutation.mutateAsync({ dto, imageFile });
    };

    const handleUpdateSecret = async (id: string, dto: ISecretDTO) => {
        if (options?.isPreviewMode) return;
        await updateMutation.mutateAsync({ id, dto });
    };

    const handleDeleteSecret = async (id: string) => {
        if (options?.isPreviewMode) return;
        await deleteMutation.mutateAsync(id);
    };

    const filteredCards = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return cards;

        return cards.filter((c) => {
            const title = (c.title ?? "").toLowerCase();
            const user = (c.usernameEmail ?? "").toLowerCase();
            return title.includes(q) || user.includes(q);
        });
    }, [cards, search]);

    const isVerified = (id: string) => (verifiedUntilById[id] ?? 0) > Date.now();

    const handleVerify = (id: string) => {
        setVerifiedUntilById((prev) => ({
            ...prev,
            [id]: Date.now() + HIGH_SECURITY_VERIFY_SECONDS * 1000,
        }));
    };

    const buildSecretDetail = (card: VaultCardUI) => {
        const secret = secretById[card.id];
        if (!secret) return null;

        const hs = secret.highSecurity ?? card.highSecurity;

        return {
            id: card.id,
            title: secret.title,
            usernameEmail: secret.usernameEmail,
            password: secret.password,
            websiteUrl: secret.websiteUrl ?? secret.website,
            notes: secret.notes,
            imageUrl: secret.imageUrl ?? card.imageUrl,
            highSecurity: hs,
            modifiedAt: secret.updatedAt ?? "today",
            modifiedBy: "System",
            createdAt: secret.createdAt ?? "N/A",
            roles: ["Engineering"],
            securityHealth: {
                status: hs ? "strong" : "medium",
                lastAudit: "N/A",
            },
        };
    };

    return {
        cards,
        search,
        setSearch,
        secretById,
        filteredCards,
        handleAddSecret,
        handleUpdateSecret,
        handleDeleteSecret,
        isVerified,
        handleVerify,
        buildSecretDetail,
        sendTwoFaTrigger,
        vaultRemainingSeconds,
        startVaultTimer,
        stopVaultTimer,
    };
};

